import { StoreAddService } from './../../../map/store-details-wizrd/store-add.service';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RegionType } from '../../../core/location/region-type.enum';
import { SpinnerSize } from '../../../core/util/spinner/spinner-size.enum';
import { AppState } from '../../../app-state';
import { Store as NgrxStore } from '@ngrx/store';
import { DetailState, selectDetailState } from '../../detail-state';
import {
  catchError,
  debounceTime,
  filter,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { FeatureCollection, Store } from '../../../core/models';
import { Observable, of, throwError } from 'rxjs';
import { LocationService } from '../../../core/location/location.service';
import { ToastService } from '../../../core/toast/toast.service';
import { DemographicFieldType } from '../../../core/depot/demographic-field-type.enum';
import * as _ from 'lodash';
import { AuthorizationAwareComponent } from '../../../core/authorization-aware-component';
import { SAMPLE_DEMOGRAPHIC_DATA } from './sample-demographic-data';
import { WizardRunnerService } from '../../../core/wizard/wizard-runner.service';
import { DemographicDetailsWizard } from './demographic-details-wizard/demographic-details-wizard';
import { formatAsCurrency } from '../../../core/util/string-utils';
import { PartnerType } from '../../../core/footer/partner-attribution/partner-type.enum';
import { round } from '../../../core/util/math-utils';
import { DetailSetDemographicsAction } from '../../detail-actions';
import { StorageService } from '../../../core/util/storage.service';
import { DemographicDataView } from '../../../core/depot/demographic-data-view';
import { DemographicsService } from '../../../core/depot/demographics.service';
import { CircleRequest } from '../../../core/shared/shape/circle-request';
import { ReportService } from '../../../core/service/report.service';
import { IsochroneService } from 'src/app/core/depot/isochrone.service';
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import {
  USER_TRACKING_ACTION_DESCRIPTIONS,
  USER_TRACKING_ACTIONS,
  USER_TRACKING_SOURCES,
} from 'src/app/core/constants/user-tracking.constants';
import { DemographicDataService } from '../../../core/services/demographic-data.service';

@Component({
  standalone: false,
  selector: 'mtn-store-demographic-profile-panel',
  templateUrl: './store-demographic-profile-panel.component.html',
  styleUrls: ['./store-demographic-profile-panel.component.scss'],
})
export class StoreDemographicProfilePanelComponent
  extends AuthorizationAwareComponent
  implements OnInit {
  private readonly VALIDATORS_DRIVE_TIME = [
    Validators.min(1),
    Validators.max(20),
  ];
  private readonly VALIDATORS_RING = [Validators.min(1), Validators.max(10)];

  public readonly PANEL_DESCRIPTION =
    "This is a profile of the population in this store's local area. Consider this profile to be the first step towards understanding this store's target customer base, and how the local demographics may be affecting this store's performance.";
  private readonly WHITE_LABEL = 'White';
  private readonly BLACK_LABEL = 'Black';
  private readonly HISPANIC_LABEL = 'Hispanic';
  private readonly ASIAN_LABEL = 'Asian';
  private readonly OTHER_LABEL = 'Other';

  demographics: DemographicDataView;
  form = new UntypedFormGroup({
    region: new UntypedFormControl(RegionType.DRIVE_TIME),
    driveTimeMinutes: new UntypedFormControl(7, this.VALIDATORS_DRIVE_TIME),
    ringMiles: new UntypedFormControl(3, this.VALIDATORS_RING),
  });
  @Output() dataEmitter: any = new EventEmitter();
  @Output() regionTypeAndValueEmitter: any = new EventEmitter();
  ethnicityDataSet: any[];
  ethnicityLabels: string[] = [];
  incomeAverage: string;
  incomeMedian: string;
  isLargeArea = false;
  isLoading = false;
  marketArea: any;
  PartnerType = PartnerType;
  pop2yrChangeText: string;
  pop5yrProjectionText: string;
  round = round;
  @Input()
  storeAnalysys: boolean = false;
  @Input()
  coardinates: any;
  @Input()
  location: any;
  RegionType = RegionType;
  SpinnerSize = SpinnerSize;
  marketAreaDemographics: any;
  driveTimeMinutesCoardinate: any;
  ringMilesCoardinate: any;

  private store: Store;
  private tradeArea: FeatureCollection;
  marketName: any;

  constructor(
    private demographicsService: DemographicsService,
    private locationService: LocationService,
    private reportService: ReportService,
    protected ngrxStore: NgrxStore<AppState>,
    private storageService: StorageService,
    private toaster: ToastService,
    private isochrone: IsochroneService,
    private storeAddservices: StoreAddService,
    private hitTracking: HitTrackingService,
    private wizardRunner: WizardRunnerService,
    private demographicDataService: DemographicDataService
  ) {
    super(ngrxStore);
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.storeAnalysys) {
      this.getDetailsForPopUp();
      let regionValue;

      if (this.form?.value.region === 'DRIVE_TIME') {
        regionValue = `${this.form?.value.driveTimeMinutes} minutes`;
      } else if (this.form?.value.region === 'RING') {
        regionValue = `${this.form?.value.ringMiles} miles`;
      }

      this.regionTypeAndValueEmitter.emit({
        regionType: this.form?.value.region,
        regionValue: regionValue,
      });
    }
  }

  loadMarketArea(): Observable<any> {
    this.isLoading = true;
    return this.storeAddservices.loadMarketArea(this.coardinates).pipe(
      switchMap((result: FeatureCollection) => this.loadData(result)),
      tap((result) => {
        this.demographics = result;
        this.marketAreaDemographics = result;
        this.isLoading = false;
        this.aggregateDemographics('load Market Area');
      }),
      catchError((err) => {
        this.isLoading = false;
        console.error('Error loading market area:', err);
        return throwError(() => err);
      })
    );
  }

  getDetailsForPopUp() {
    this.marketArea = true;
    this.loadMarketArea().subscribe({
      next: () => {
        this.load7minutes(7); // Executes only after loadMarketArea() completes
      },
      error: (err) => {
        console.error('Error in getDetailsForPopUp:', err);
      },
    });
  }

  load7minutes(minutes: number) {
    this.isLoading = true;
    const request: any = {
      point: {
        lat: this.coardinates.lat,
        lng: this.coardinates.lng,
      },
      travelTimeMinutes: minutes,
    };

    this.isochrone.findOneIsochrone(request).subscribe({
      next: (result: FeatureCollection) => {
        this.driveTimeMinutesCoardinate = result;
        this.loadData(result).subscribe({
          next: (result) => {
            this.demographics = result;
            this.isLoading = false;
            this.aggregateDemographics('load drive time');
          },
          error: (err) => {
            this.isLoading = false;
            console.error('Error fetching demographic data:', err);
          },
        });
      },
      error: (err: any) => {
        this.isLoading = false;
        console.error('Error loading market area:', err);
      },
    });
  }

  loadRingMiles(miles: number) {
    this.isLoading = true;
    const circleRequest: CircleRequest = new CircleRequest();
    circleRequest.point = {
      type: 'Point',
      coordinates: [this.coardinates.lat, this.coardinates.lng],
    };
    circleRequest.distanceMiles = miles;
    return this.reportService.createOne(circleRequest).subscribe({
      next: (result: FeatureCollection) => {
        this.ringMilesCoardinate = this.convertToGeoJSON(result);
        this.loadData(this.ringMilesCoardinate).subscribe({
          next: (result) => {
            this.demographics = result;
            this.isLoading = false;
            this.aggregateDemographics('load Ring Miles');
          },
          error: (err) => {
            this.isLoading = false;
            console.error('Error fetching demographic data:', err);
          },
        });
      },
      error: (err: any) => {
        this.isLoading = false;
        console.error('Error loading market area:', err);
      },
    });
  }
  convertToGeoJSON(data: any): any {
    // Deep clone the data to avoid modifying the original object
    const updatedData = JSON.parse(JSON.stringify(data));

    updatedData.features.forEach((feature: any) => {
      if (feature.geometry && feature.geometry.coordinates) {
        feature.geometry.coordinates = feature.geometry.coordinates.map(
          (polygon: any) =>
            polygon.map((coordinate: any) => [coordinate[1], coordinate[0]]) // Switch [lat, lon] to [lon, lat]
        );
      }
    });

    return updatedData;
  }

  loadData(marketAreaCoardinate: any) {
    const request: any = {
      geometries: [
        {
          geometry: marketAreaCoardinate.features[0].geometry,
        },
      ],
    };

    return this.demographicsService.search(request).pipe(
      map((results: DemographicDataView[]) => {
        if (results.length === 1) {
          return results[0];
        } else {
          throw 'Unexpected result count';
        }
      })
    );
  }

  onAuthorizationChange(): void { }

  onAuthorizationInit(): void {
    this.init();
  }

  openDetailsWizard(): void {
    const wizard = new DemographicDetailsWizard();
    wizard.model = {
      demographics: this.demographics,
      marketArea: this.marketArea,
      store: this.store,
      region: this.form.get('region').value,
      ringMiles: this.form.get('ringMiles').value,
      driveTimeMinutes: this.form.get('driveTimeMinutes').value,
    };

    this.wizardRunner.run(wizard);
  }

  private aggregateDemographics(from?: string): void {
    console.log(from);
    this.buildEthnicityDataSet();

    const population2YearChangePercent =
      this.demographics.populationHistoricalGrowth2Year;
    const changeSign = population2YearChangePercent > 0 ? '+' : '';
    this.pop2yrChangeText = `${changeSign}${round(
      population2YearChangePercent * 100,
      2
    ).toFixed(2)}%`;

    const projection = this.demographics.populationProjectedGrowth5Year;
    const projectionSign = projection > 0 ? '+' : '';
    this.pop5yrProjectionText = `${projectionSign}${round(
      projection * 100,
      2
    ).toFixed(2)}%`;

    this.incomeAverage = formatAsCurrency(
      this.demographics.householdsAverageIncome,
      false
    );
    this.incomeMedian = formatAsCurrency(
      this.demographics.householdsMedianIncome,
      false
    );

    this.isLoading = false;
    this.emitData();
  }

  emitData() {
    const demographicData = [
      {
        category: 'Ethnicity',
        data: this.getEthnicityData(),
      },
      {
        category: 'Population',
        data: {
          'Historical 2 Year Growth': `${this.pop2yrChangeText}`,
          'Total Population ': `${this.formatWholeNumber(
            this.demographics.populationEstimate
          )}`,
          'Projected 5 Year Growth ': `${this.pop5yrProjectionText}`,
        },
      },
      {
        category: 'People',
        data: {
          'Average Age': `${this.demographics.ageAverage?.toFixed(1)}`,
          'College Educated': `${round(
            this.demographics.educationCollegeOrDegree * 100,
            2
          ).toFixed(2)}%`,
          'Average Household Size': `${this.demographics.householdsPersonsPer?.toFixed(
            1
          )}`,
        },
      },
      {
        category: 'Income',
        data: {
          'Average Income ': `${this.formatNumberWithTwoDecimals(
            this.incomeAverage
          )}`,
          'Median Income ': `${this.formatNumberWithTwoDecimals(
            this.incomeMedian
          )}`,
        },
      },
    ];
    this.dataEmitter.emit(demographicData);
    let regionValue;

    if (this.form?.value.region === 'DRIVE_TIME') {
      regionValue = `${this.form?.value.driveTimeMinutes} minutes`;
    } else if (this.form?.value.region === 'RING') {
      regionValue = `${this.form?.value.ringMiles} miles`;
    }

    this.regionTypeAndValueEmitter.emit({
      regionType: this.form?.value.region,
      regionValue: regionValue,
    });

    // Update service with demographic data
    this.demographicDataService.updateDemographicData({
      demographics: this.demographics,
      regionType: this.form?.value.region,
      regionValue: regionValue
    });
  }
  getEthnicityData() {
    const ethnicityData: any = {};
    this.ethnicityLabels.forEach((element: any, index: any) => {
      ethnicityData[element] = `${this.ethnicityDataSet[0].data[index]}%`;
    });
    return ethnicityData;
  }

  private buildEthnicityDataSet(): void {
    const valueWrappers: any[] = [];

    valueWrappers.push({
      type: DemographicFieldType.ETHNICITY_WHITE,
      numericValue: round(this.demographics.ethnicityWhite * 100, 1),
      value: round(this.demographics.ethnicityWhite * 100, 1).toFixed(1),
    });

    valueWrappers.push({
      type: DemographicFieldType.ETHNICITY_BLACK,
       numericValue: round(this.demographics.ethnicityBlack * 100, 1),
      value: round(this.demographics.ethnicityBlack * 100, 1).toFixed(1),
    });

    valueWrappers.push({
      type: DemographicFieldType.ETHNICITY_HISPANIC,
      numericValue: round(this.demographics.ethnicityHispanic * 100, 1),
      value: round(this.demographics.ethnicityHispanic * 100, 1).toFixed(1),
    });

    valueWrappers.push({
      type: DemographicFieldType.ETHNICITY_ASIAN,
      numericValue: round(this.demographics.ethnicityAsian * 100, 1),
      value: round(this.demographics.ethnicityAsian * 100, 1).toFixed(1),
    });

    valueWrappers.push({
      type: DemographicFieldType.ETHNICITY_OTHER,
      numericValue: round(this.demographics.ethnicityOther * 100, 1),
      value: round(this.demographics.ethnicityOther * 100, 1).toFixed(1),
    });

    const data: number[] = [];
    const labels: string[] = [];

    const orderedValueWrappers = _.orderBy(valueWrappers, 'numericValue', 'desc');
    orderedValueWrappers.forEach((wrapper: any) => {
      data.push(wrapper.value);

      switch (wrapper.type) {
        case DemographicFieldType.ETHNICITY_WHITE:
          labels.push(this.WHITE_LABEL);
          break;
        case DemographicFieldType.ETHNICITY_BLACK:
          labels.push(this.BLACK_LABEL);
          break;
        case DemographicFieldType.ETHNICITY_HISPANIC:
          labels.push(this.HISPANIC_LABEL);
          break;
        case DemographicFieldType.ETHNICITY_ASIAN:
          labels.push(this.ASIAN_LABEL);
          break;
        case DemographicFieldType.ETHNICITY_OTHER:
          labels.push(this.OTHER_LABEL);
          break;
      }
    });

    this.ethnicityDataSet = [
      {
        data: data,
        label: 'Ethnicity Data Set',
      },
    ];

    this.ethnicityLabels = labels;
  }

  private getSelectedRegionShape(
    regionType: RegionType
  ): Observable<FeatureCollection> {
    this.isLargeArea = false;
    switch (regionType) {
      case RegionType.DRIVE_TIME:
        const minutes = this.form.get('driveTimeMinutes').value;

        if (minutes === 7) {
          return selectDetailState(this.ngrxStore).pipe(
            filter((state: DetailState) => !!state.defaultDriveTime),
            take(1),
            map((state: DetailState) => state.defaultDriveTime)
          );
        } else {
          if (minutes > 10) {
            this.isLargeArea = true;
          }
          return this.locationService.findOnesIsochrone(
            this.store.space.location.uuid,
            minutes
          );
        }
      case RegionType.MARKET_AREA:
        this.isLargeArea = true;
        return of(this.marketArea);
      case RegionType.RING:
        const radius = this.form.get('ringMiles').value;
        this.isLargeArea = radius > 5;
        /*const circle = new google.maps.Circle({
          center: this.store.space.location.getPointAsLatLng(),
          radius: radius
        });*/
        const circleRequest: CircleRequest = new CircleRequest();
        circleRequest.point = {
          type: 'Point',
          coordinates: [
            this.store.space.location.getPointLongitude(),
            this.store.space.location.getPointLatitude(),
          ],
        };
        circleRequest.distanceMiles = radius;

        return this.reportService.createOne(circleRequest);
      case RegionType.TRADE_AREA:
        return of(this.tradeArea);
    }
  }

  private init(): void {
    this.storageService
      .get(StorageService.DEMOGRAPHIC_PROFILE_REGION)
      .subscribe((value: RegionType) => {
        // Check if stored region value differs from current form value
        if (value && value !== this.form.get('region').value) {
          this.form.get('region').setValue(value, { emitEvent: false });
        }
      });

    this.addSubscription(
      selectDetailState(this.ngrxStore)
        .pipe(debounceTime(300))
        .pipe(
          filter(
            (state: DetailState) =>
              !!state.store && (!this.hasStandardLicense || !!state.marketArea)
          )
        )
        .pipe(
          take(1),
          switchMap((state: DetailState) => {
            this.store = state.store;
            const feature = state.marketArea?.getFeature();
            this.marketName = feature?.properties ? feature.properties['name'] : null;
            this.marketArea = state.marketArea;
            this.tradeArea = state.tradeArea;

            return this.loadDemographics();
          })
        )
        .subscribe()
    );

    this.addSubscription(
      this.form.get('region').valueChanges.subscribe((value: RegionType) => {
        this.storageService
          .set(StorageService.DEMOGRAPHIC_PROFILE_REGION, value)
          .subscribe();

        //Update driveTimeMinutes validators
        const driveTimeMinutesValidators = [...this.VALIDATORS_DRIVE_TIME];
        if (value === RegionType.DRIVE_TIME) {
          driveTimeMinutesValidators.push(Validators.required);
        }
        this.form
          .get('driveTimeMinutes')
          .setValidators(driveTimeMinutesValidators);

        //Update ringMiles validators
        const ringMilesValidators = [...this.VALIDATORS_RING];
        if (value === RegionType.RING) {
          ringMilesValidators.push(Validators.required);
        }
        this.form.get('ringMiles').setValidators(ringMilesValidators);

        this.form.updateValueAndValidity({ emitEvent: false });
      })
    );

    this.addSubscription(
      this.form.valueChanges.pipe(debounceTime(600)).subscribe(() => {
        const location = this.store?.space?.location;
        const shoppingCenter = this.store?.space?.shoppingCenter;
        const regionControl = this.form.get('region')?.value;
        const driveTime = this.form.get('driveTimeMinutes')?.value;
        const ringMiles = this.form.get('ringMiles')?.value;
        let region: string;
        if (regionControl === 'DRIVE_TIME') {
          if (driveTime < 1 || driveTime > 20) {
            return;
          }
          region = `${driveTime} minutes`;


        } else if (regionControl === 'RING') {
          if (ringMiles < 1 || ringMiles > 10) {
            return;
          }
          region = `${ringMiles} miles`;
        } else {
          region = 'Market Area';
        }

        let metadata;
        if (this.store) {
          metadata = {
            region: region,
            data: {
              storeId: this.store.uuid,
              storeName: this.store.name,
              addressLine1: location?.addressLine1,
              city: location?.city,
              cbsa: this.marketName,
              state: location?.state,
              postalCode: location?.postalCode,
              county: location?.county,
              shoppingCenterName: shoppingCenter?.name,
              landlord: shoppingCenter?.ownerCompany?.name

            },
          };
        } else {
          metadata = {
            region: region,
            location: this.location ? this.location : null
          };
        }


        // Fire hit tracking in parallel — do not block data load on it
        this.hitTracking
          .hitTheUserTracking(
            USER_TRACKING_SOURCES.STORE_DEMOGRAPHIC_PROFILE_PANEL,
            metadata,
            USER_TRACKING_ACTIONS.DEMOGRAPHIC_REGION_CHANGED,
            USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.DEMOGRAPHIC_REGION_CHANGED]
          )
          .pipe(catchError((err) => {
            console.warn('Hit tracking failed silently', err);
            return of(null);
          }))
          .subscribe();

        if (this.form.valid) {
          if (!this.storeAnalysys) {
            this.loadDemographics().subscribe();
          } else {
            const formValue = this.form.getRawValue();
            this.isLoading = true;
            if (formValue.region == 'MARKET_AREA') {
              this.demographics = this.marketAreaDemographics;
              this.loadMarketArea().subscribe({
                next: () => {},
                error: (err) => {
                  console.error('Error in loadMarketArea subscription:', err);
                }
              });
            } else if (formValue.region == 'DRIVE_TIME') {
              this.load7minutes(formValue.driveTimeMinutes);
            } else if (formValue.region == 'RING') {
              this.loadRingMiles(formValue.ringMiles);
            }
          }
        }


      })
    );
  }

  private loadDemographics(): Observable<DemographicDataView> {
    this.isLoading = true;
    const formValue = this.form.getRawValue();
    if (this.hasStandardLicense) {
      return this.getSelectedRegionShape(formValue.region).pipe(
        catchError((err) => {
          console.error(err);
          this.toaster.error(
            "Uh-oh! We weren't able to load the Demographic Profile for this store. If the problem persists, please contact an administrator for assistance."
          );
          this.isLoading = false;
          return of(null);
        }),
        switchMap((regionGeoJson: FeatureCollection) => {
          return this.demographicsService
            .findDemographicsForFeatureCollection(regionGeoJson)
            .pipe(
              tap((result: DemographicDataView) => {
                this.demographics = result;
                this.ngrxStore.dispatch(
                  DetailSetDemographicsAction({ demographics: result })
                );
                this.aggregateDemographics('initial load');
              })
            );
        })
      );
    } else {
      this.isLoading = false;
      this.demographics = SAMPLE_DEMOGRAPHIC_DATA;
      this.ngrxStore.dispatch(
        DetailSetDemographicsAction({
          demographics: SAMPLE_DEMOGRAPHIC_DATA,
          isDemographicsSampleData: true,
        })
      );
      this.aggregateDemographics();
      return of(null);
    }
  }

  formatNumberWithTwoDecimals(number) {
    return number.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  formatWholeNumber(number) {
    return number.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
}
